import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, CloudIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const SERVICES = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75z" />
      </svg>
    ),
    title: "Cloud Migration",
    desc: "Lift-and-shift or full re-architecture — we assess your stack, define the migration path, and execute with zero data loss and minimal downtime.",
    color: "border-sky-500/30 bg-sky-500/5",
    accent: "text-sky-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25z" />
      </svg>
    ),
    title: "Infrastructure as Code",
    desc: "Every resource declared in Terraform or Pulumi. Version-controlled, reviewable, reproducible infra across dev, staging, and production.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
    ),
    title: "CI/CD Pipelines",
    desc: "GitHub Actions, GitLab CI, or Bitrise pipelines with automated testing, staging deployments, security scans, and one-click production promotions.",
    color: "border-indigo-500/30 bg-indigo-500/5",
    accent: "text-indigo-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
    ),
    title: "Kubernetes & Containers",
    desc: "Production-grade K8s clusters (EKS / GKE / AKS) with Helm charts, autoscaling, rolling deployments, and Istio service mesh.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
      </svg>
    ),
    title: "Cost Optimisation",
    desc: "Reserved instances, right-sizing, spot strategies, and FinOps dashboards. Typical clients cut cloud spend 25–40% in the first quarter.",
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 13.036h.008v.008H12v-.008z" />
      </svg>
    ),
    title: "Security & Compliance",
    desc: "VPC architecture, IAM least-privilege, WAF, DDoS protection, SOC 2 / HIPAA / PCI DSS alignment, and pen-test remediation.",
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
  },
];

const PLATFORMS = [
  { name: "AWS", desc: "EKS, Lambda, RDS, S3, CloudFront, Route53, WAF", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/30" },
  { name: "GCP", desc: "GKE, Cloud Run, BigQuery, Firestore, Cloud CDN", color: "text-sky-400", bg: "bg-sky-500/10 border-sky-500/30" },
  { name: "Azure", desc: "AKS, App Service, CosmosDB, Azure DevOps, AD", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/30" },
];

const TECH = [
  "Terraform", "Kubernetes", "Docker", "Helm", "GitHub Actions",
  "Datadog", "Prometheus", "Grafana", "Vault", "Istio", "ArgoCD", "Pulumi",
];

const FEATURES = [
  "99.9%+ uptime SLA design",
  "Multi-region failover",
  "Zero-downtime deployments",
  "Infrastructure as Code (IaC)",
  "Security scanning in CI",
  "Cost alerts & budgets",
  "Disaster recovery planning",
  "Auto-scaling policies",
  "Log aggregation & alerting",
  "Secrets management",
  "Network segmentation (VPC)",
  "Compliance documentation",
];

export default function CloudDetail() {
  useSEO({
    title: "Cloud Solutions & DevOps | AWS, GCP, Azure | BerryByte",
    description: "Expert cloud migration, infrastructure-as-code, and DevOps services for US & Canadian businesses. AWS, GCP & Azure specialists. Cut cloud spend 25-40%.",
    keywords: "cloud solutions company, AWS development services, cloud migration services US Canada, DevOps agency, Kubernetes deployment, infrastructure as code Terraform, cloud cost optimization",
    canonical: "https://www.berrybyteinn.com/services/cloud-solutions",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-sky-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-indigo-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <CloudIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-sky-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / Cloud Solutions
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
              AWS · GCP · Azure · Terraform · Kubernetes
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Cloud Without<br />
              <span className="bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent">the Headaches.</span>
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              Cloud migrations, infrastructure-as-code, and zero-downtime CI/CD pipelines. We help US & Canadian businesses cut cloud costs 25–40% while improving reliability.
            </p>
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-sky-600 to-indigo-600 text-white font-semibold shadow-2xl shadow-sky-900/40 hover:shadow-sky-900/60 hover:-translate-y-1 transition-all duration-300">
              Start Your Project<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "25-40%", label: "Avg Cost Reduction" }, { value: "99.9%", label: "Uptime Target" }, { value: "3", label: "Major Cloud Providers" }, { value: "0", label: "Downtime Migrations" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-sky-400 font-semibold text-sm tracking-widest uppercase">Cloud Platforms</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">We're certified across<br /><span className="bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent">all major clouds.</span></h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mb-16">
            {PLATFORMS.map((p) => (
              <div key={p.name} className={`rounded-2xl border p-8 ${p.bg} text-center hover:-translate-y-1 transition-transform duration-300`}>
                <div className={`text-4xl font-black mb-3 ${p.color}`}>{p.name}</div>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-sky-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">Services</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Cloud engineering,<br /><span className="bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent">end-to-end.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-sky-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Our cloud toolset.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-sky-500/20 bg-sky-500/5 text-sky-300 text-sm font-medium hover:bg-sky-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Production-ready out of the box.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-sky-500/20 border border-sky-500/30 flex items-center justify-center text-sky-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-sky-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">Ready to move<br /><span className="bg-gradient-to-r from-sky-400 to-indigo-400 bg-clip-text text-transparent">to the cloud?</span></h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">Get a free cloud audit. We'll identify cost savings and reliability improvements in your current setup.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-sky-600 to-indigo-600 text-white font-semibold shadow-2xl shadow-sky-900/40 hover:shadow-sky-900/60 hover:-translate-y-1 transition-all duration-300">
              Get a Free Cloud Audit<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
